import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Card } from 'tea-component';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import Filter from '@/pages/intelligence/components/Filter';
import {
  resolveDataSourceFilterValues,
  type TDataSourceFilterOption,
} from '../../../utils';

import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';

interface ISourceFilterProps {
  filterConfirm: (value: Record<string, unknown>) => void;
  filterReset?: () => void;
  sourceValue?: string[];
  dataSourceOptions?: TDataSourceFilterOption[];
}

export default function SourceFilter(props: ISourceFilterProps) {
  const {
    filterConfirm,
    filterReset,
    sourceValue = [],
    dataSourceOptions = [],
  } = props;

  const defaultValue = {
    [filterKeyMap.DATA_SOURCE_FULL]: { value: [] },
    [filterKeyMap.GAMETYPE_FULL]: { value: ['Mobile', 'PC', 'Console'] },
    [filterKeyMap.SERVICE_TYPE_FULL]: { value: ['Data acquisition', 'Data crawling'] },
  };

  const { setFilterExtend, resetFilterExtend } = useFilterExtend({});

  const resolvedSourceValues = useMemo(
    () => resolveDataSourceFilterValues(sourceValue, dataSourceOptions),
    [sourceValue, dataSourceOptions],
  );

  const buildFormValue = useCallback(
    () => ({
      [filterKeyMap.DATA_SOURCE_FULL]: { value: resolvedSourceValues },
      [filterKeyMap.GAMETYPE_FULL]: { value: ['Mobile', 'PC', 'Console'] },
      [filterKeyMap.SERVICE_TYPE_FULL]: { value: ['Data acquisition', 'Data crawling'] },
    }),
    [resolvedSourceValues],
  );

  const [formValue, setFormValue] = useState(buildFormValue);
  const formValueRef = useRef(formValue);
  const urlFilterAppliedRef = useRef(false);

  const companyRef = useRef();
  const gametypeRef = useRef();
  const servicetypeRef = useRef();

  const buildInputList = (options: TDataSourceFilterOption[]) => {
    const defaultInputList = {
      DATA_SOURCE_FULL: {
        key: filterKeyMap.DATA_SOURCE_FULL,
        default: defaultValue[filterKeyMap.DATA_SOURCE_FULL],
        ref: companyRef,
        options,
      },
      GAMETYPE_FULL: {
        key: filterKeyMap.GAMETYPE_FULL,
        default: defaultValue[filterKeyMap.GAMETYPE_FULL],
      },
      SERVICE_TYPE_FULL: {
        key: filterKeyMap.SERVICE_TYPE_FULL,
        default: defaultValue[filterKeyMap.SERVICE_TYPE_FULL],
      },
    };

    return [
      defaultInputList.DATA_SOURCE_FULL,
      defaultInputList.GAMETYPE_FULL,
      defaultInputList.SERVICE_TYPE_FULL,
    ];
  };

  const [inputList, setInputList] = useState(() => buildInputList(dataSourceOptions));

  useEffect(() => {
    setInputList(buildInputList(dataSourceOptions));
  }, [dataSourceOptions]);

  useEffect(() => {
    urlFilterAppliedRef.current = false;
  }, [sourceValue.join(',')]);

  useEffect(() => {
    const nextFormValue = buildFormValue();
    setFormValue(nextFormValue);
    formValueRef.current = nextFormValue;
  }, [buildFormValue]);

  useEffect(() => {
    if (!dataSourceOptions.length || !resolvedSourceValues.length || urlFilterAppliedRef.current) {
      return;
    }
    urlFilterAppliedRef.current = true;
    filterConfirm({
      source: resolvedSourceValues,
      game_type: ['Mobile', 'PC', 'Console'],
      service_type: ['Data acquisition', 'Data crawling'],
    });
  }, [dataSourceOptions, resolvedSourceValues, filterConfirm]);

  const onConfirm = useCallback(
    (value /** 筛选器数据 */, apiValue /** 筛选器转换后的数据 */) => {
      formValueRef.current = value;
      setFormValue(value);
      const filterForm = {
        source: value[filterKeyMap.DATA_SOURCE_FULL]?.value,
        game_type: value[filterKeyMap.GAMETYPE_FULL]?.value,
        service_type: value[filterKeyMap.SERVICE_TYPE_FULL]?.value,
      };
      filterConfirm(filterForm);
      setFilterExtend(apiValue);
    },
    [filterConfirm, setFilterExtend],
  );

  const onReset = useCallback(
    () => {
      formValueRef.current = {
        ...defaultValue,
      };
      companyRef?.current?.reset();
      gametypeRef?.current?.reset();
      servicetypeRef?.current?.reset();
      resetFilterExtend();
      if (filterReset instanceof Function) {
        filterReset();
      }
    },
    [filterReset, resetFilterExtend],
  );

  const filterKey =
    dataSourceOptions.map(option => option.value).join('|') || 'empty';

  return (
    <Card>
      <Card.Body>
        <Filter
          key={filterKey}
          value={formValue}
          inputList={inputList}
          onConfirm={onConfirm}
          onReset={onReset}
          defaultValue={defaultValue}
        />
      </Card.Body>
    </Card>
  );
}
